import { useI18n } from 'vue-i18n';
import { type GenericObject, useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { z, ZodObject, type ZodRawShape } from 'zod';
import { type Ref, watch } from 'vue';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useReactiveForm<TShape extends ZodRawShape, TSchema extends ZodObject<TShape>>(
    schema: TSchema,
    initialValues?: Partial<GenericObject>
) {
    const { locale } = useI18n();

    const form = useForm({
        validationSchema: toTypedSchema(schema),
        initialValues,
    });

    watch(locale, async () => {
        for (const value of Object.keys(form.errors.value)) {
            if (value !== '') {
                await form.validateField(value);
            }
        }
    });

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const fields = {} as {
        [K in keyof z.infer<TSchema>]-?: Ref<z.infer<TSchema>[K]>;
    };

    for (const key of Object.keys(schema.shape)) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const casedKey = key as keyof typeof fields;
        fields[casedKey] = form.defineField(key)[0];
    }

    return { ...form, fields };
}
